function getZoomImageConsumePower(width: number, height: number, zoom: number) {
  if (zoom === 1) {
    return 0
  }

  const number = width * height * zoom;

  if (number <= 2048 * 2048) { // 2048 像素内消耗 1 算力
    return 1;
  } else if (number <= 4096 * 4096) {// 2048 像素外消耗 2 算力
    return 2;
  }
  return 4;
}

import type { Module } from '@/types/artwork/generate';
import { toValue } from 'vue';
import { useUserStore } from '@/store/user';
import { useArtworkStore } from '@/store/artwork';
import { ModuleEnum } from '@/assets/artwork';
import { useTeamTrialStore } from '@/store/teamTrial';

// 根据 params 计算当前所需要消耗的算力
export function getConsumePower(params: any) {
  const {
    module,
    layoutRefer,
    faceDetail,
    styleReferList,
    rescaleFactor,
    batchSize,
    hdFix,
    hdScale,
    alchemy,
    aspectRatios,
  } = params || {};
  const { width, height } = toValue(layoutRefer) || { width: 0, height: 0 };

  let basePower = 0; // 单张图需要消耗的算力

  const userStore = useUserStore();
  const useVipPackage = userStore.highestPackage;

  const artworkStore = useArtworkStore();

  // 面部精修消耗 1 算力
  if (faceDetail) {
    basePower++;
  }

  // 语义增强引擎
  if (alchemy) {
    basePower++;
  }

  // 形象样式参考消耗 1 算力
  if ([1].includes(module) && Array.isArray(styleReferList) && styleReferList.length > 0) {
    basePower++;
  }

  // 高清修复
  if (hdFix) {
    basePower += hdScale === 1.5 ? 1 : 2;
  }

  // Flux模型根据尺寸消耗算力（一键成稿、3D转换）
  if (artworkStore.isFlux && (module === ModuleEnum['explore'] || module === ModuleEnum['threeTransform'])) {
    if (layoutRefer) {
      basePower += 2;
    } else {
      switch (aspectRatios) {
        case '3:3':
          basePower += 0;
          break;
        case '2:2':
          basePower += 1;
          break;
        default:
          basePower += 2;
          break;
      }
    }
  }

  const channel = artworkStore.channel;

  switch (module as Module & 9) {
    case ModuleEnum['explore']: // 一键成稿每张图消耗 1 算力
      if (useVipPackage !== 'svip' || channel === 'expedite') {
        basePower++;
      }
      break;
    case ModuleEnum['threeTransform']:
      // basePower += 3;
      if (useVipPackage !== 'svip' || channel === 'expedite') {
        basePower++;
      }
      break;
    case ModuleEnum['colour']: // 丰富细节每张图消耗 2 算力（HOLO - V1模型每张5算力）
      basePower += artworkStore.isFlux ? 5 : 2;
      break;
    case ModuleEnum['refine']: // 局部细化每张图消耗 1 算力（HOLO - V1模型每张3算力）
      basePower += artworkStore.isFlux ? 3 : 1;
      break;
    case ModuleEnum['sketch']: // 线稿提取
      basePower += 3;
      break;
    case ModuleEnum['fission']: // 相似图裂变
      basePower += 3;
      break;
    case 9: // 外部放大功能
      basePower += getZoomImageConsumePower(width, height, rescaleFactor / 100);
      break;
    case ModuleEnum['replace']:
      // smearType 1：替换模式 2：擦除模式
      basePower += (params.smearType == 2 ? 1 : 2);
      break;
    case ModuleEnum['matting']:
      basePower++;
      break;
    case ModuleEnum['zoom']:
      basePower += getZoomImageConsumePower(width, height, rescaleFactor);
      break;
    case ModuleEnum['expansion']:
      basePower += 3;
      break;
    case ModuleEnum['pixelate']:
      basePower += 1;
      break;
  }

  const consumePower = basePower * (batchSize || 1);

  return consumePower;
}


// 获取算力消耗文案
import { h } from 'vue';
import PowerIcon from '@/components/SvgIcon/PowerIcon.vue';
import { useTeamEditionStore } from '../teamEdition';
import { RenderAspectRatios, PerspectiveType, PerspectiveBackground } from '@/assets/artwork/options';
import { ModuleId, type ModuleIds } from '@xmiles/holopix-types';
import { CANVAS_QWEN_FREE_PERSPECTIVE_CONSUME_POWER } from '@/constants';


export function getConsumePowerLabel(power: number, {
  suffix = undefined as string | undefined,
  // 是否忽略算力不足的提示
  ignorePowerExhausted = false,
  // 是否忽略已到期提示
  ignoreTrialExpired = false,
  param = undefined as any, // 生图参数（包含模块相关字段）
  activityActive = true as boolean, // 是否处于活动期（影响是否显示0算力）
} = {}) {
  const artworkStore = useArtworkStore();
  const teamTrialStore = useTeamTrialStore();
  const isTrialExpired = teamTrialStore.isTrialExpired;
  const isPowerExhausted = teamTrialStore.isPowerExhausted;
  const teamEditionStore = useTeamEditionStore();
  const userStore = useUserStore();

  const isVip = userStore.isMemberUser;
  const excludedViews = [PerspectiveType.ThreeView, PerspectiveType.SixView];
  if (!isVip) {
    excludedViews.push(PerspectiveType.Top, PerspectiveType.Bottom, PerspectiveType.Back);
  }

  if (teamEditionStore.userType === 'team') {
    // 如果已到期
    if (isTrialExpired && !ignoreTrialExpired) {
      return h(
        'span',
        {
          class: 'result-btns-text',
          style: {
            marginLeft: '4px',
            display: 'inline-flex',
            'align-items': 'center',
            'column-gap': '2px'
          }
        },
        [
          h('span', { class: 'power-number' }, '试用己到期，请开通正式版')
        ]
      );
    }

    // 如果已用完
    if (isPowerExhausted && !ignorePowerExhausted) {
      return h(
        'span',
        {
          class: 'result-btns-text',
          style: {
            marginLeft: '4px',
            display: 'inline-flex',
            'align-items': 'center',
            'column-gap': '2px'
          }
        },
        [
          h('span', { class: 'power-number' }, '算力已用完，请开通正式版')
        ]
      );
    }
  }


  const renderZeroPowerLabel = () => {
    return h(
      'span',
      {
        class: 'result-btns-text',
        style: {
          marginLeft: '4px',
          display: 'inline-flex',
          'align-items': 'center',
          'column-gap': '2px'
        }
      },
      [
        h(PowerIcon, { suffix: suffix !== undefined ? suffix : artworkStore.channel === 'expedite' ? 'dark' : '' }),
        h('span', { class: 'power-number' }, 0)
      ]
    );
  };

  const resolvedModule = param?.module;

  /** 万能渲染付费风格（workspace / 自由画布共用，兼容 type / loraType / kleinFluxType） */
  const isRenderPremiumStyle = (selectStyle?: {
    type?: number;
    loraType?: string;
    kleinFluxType?: number;
  } | null) => {
    if (!selectStyle) return false;
    if (Number(selectStyle.type) >= 1) return true;
    if (selectStyle.loraType === "KleinFlux" || selectStyle.loraType === "KleinFlux2") return true;
    if (Number(selectStyle.kleinFluxType) >= 1) return true;
    return false;
  };

  if (resolvedModule === ModuleId.Render || resolvedModule === ModuleId.CanvasRender) {
    const shouldUseFreeRender = userStore.sources.freeRenderCount > 0;
    if (shouldUseFreeRender && isRenderPremiumStyle(param?.selectStyle)) {
      return renderZeroPowerLabel();
    }
  }

  if (resolvedModule === ModuleId.FreePerspective) {
    const hasFreePerspective = userStore.sources.freePerspectiveCount > 0;

    if (hasFreePerspective) {
      const resolvedAspectRatios = param?.aspectRatios;
      const resolvedViewAngleId = param?.viewAngleId;
      const isFast = resolvedAspectRatios === RenderAspectRatios.Fast;
      const isViewAllowed = !resolvedViewAngleId || !excludedViews.includes(resolvedViewAngleId);
      const shouldUseFreePerspective = activityActive && isFast && isViewAllowed;

      if (shouldUseFreePerspective) {
        return renderZeroPowerLabel();
      }
    }
  }

  if (resolvedModule === ModuleId.CanvasQwenFreePerspective) {
    // 自由画布-自由视角：固定 15 算力，有免费次数时显示 0
    if (userStore.sources.qwenFreePerspectiveCount > 0) {
      return renderZeroPowerLabel();
    }
    return h(
      'span',
      {
        class: 'result-btns-text',
        style: {
          marginLeft: '4px',
          display: 'inline-flex',
          'align-items': 'center',
          'column-gap': '2px'
        }
      },
      [
        h(PowerIcon, { suffix: suffix !== undefined ? suffix : artworkStore.channel === 'expedite' ? 'dark' : '' }),
        h('span', { class: 'power-number' }, CANVAS_QWEN_FREE_PERSPECTIVE_CONSUME_POWER)
      ]
    );
  }

  if (resolvedModule === ModuleId.QwenFreePerspective) {
    // 一键成稿-自由视角：算力按尺寸区分（1024=5，1536=10），由父组件传入 power
    if (userStore.sources.qwenFreePerspectiveCount > 0) {
      return renderZeroPowerLabel();
    }
    // 走传入的 power（已按尺寸计算），与其他模块一致
  }


  return h(
    'span',
    {
      class: 'result-btns-text',
      style: {
        marginLeft: '4px',
        display: 'inline-flex',
        'align-items': 'center',
        'column-gap': '2px'
      }
    },
    [
      h(PowerIcon, { suffix: suffix !== undefined ? suffix : artworkStore.channel === 'expedite' ? 'dark' : '' }),
      h('span', { class: 'power-number' }, power)
    ]
  );
}

// 获取当前会员状态用于埋点上报
export function getMemberStatue() {
  const userStore = useUserStore();

  if (userStore.isMemberUser) {
    return '已开通会员';
  } else if (userStore.isExpireUser) {
    return '会员已过期';
  } else {
    return '未开通会员';
  }
}
